import { CardHeader as ContentCardHeader } from '@hue/design-system';
import { MessageCircle as CardIconMessageCircle } from 'lucide-react';
import { Button, StatusPill, Textarea, useSnackbar } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RotateCcw } from 'lucide-react';
import { useRef, useState } from 'react';
import { getApiError, whatsappTemplatesApi, type WhatsAppTemplate } from '../lib/api.js';
import { ErrorCard, LoadingCard, RequestAlert } from './data-view.js';
import { PageHeader } from './page-title.js';

/// O que cada variável diz, em português, e o exemplo que a prévia usa.
const variableGuide: Record<string, { label: string; sample: string }> = {
  clientName: { label: 'nome do cliente', sample: 'Ana Souza' },
  tenantName: { label: 'nome da empresa', sample: 'Hue Estética' },
  serviceName: { label: 'serviço', sample: 'Limpeza de pele' },
  professionalName: { label: 'profissional', sample: 'Beatriz Lima' },
  date: { label: 'data', sample: '25/09/2026' },
  time: { label: 'hora', sample: '10:30' },
};

function preview(body: string): string {
  return body.replace(
    /\{\{\s*([A-Za-z]\w*)\s*\}\}/gu,
    (whole, name: string) => variableGuide[name]?.sample ?? whole,
  );
}

/// As mensagens de WhatsApp da empresa (ADR 0036). O sistema não envia: deixa o texto pronto e abre
/// o WhatsApp de quem clicou, na conversa com o cliente.
export function WhatsAppTemplates({ canEdit }: { canEdit: boolean }) {
  const templates = useQuery({
    queryKey: ['company', 'whatsapp-templates'],
    queryFn: whatsappTemplatesApi.list,
    retry: false,
  });
  return (
    <section aria-labelledby="whatsapp-templates-title">
      <PageHeader
        trail="Configurações / Mensagens de WhatsApp"
        id="whatsapp-templates-title"
        title="Mensagens de WhatsApp"
        description="O texto que abre pronto no WhatsApp quando alguém da equipe confirma o agendamento ao cliente. Quem clica ainda confere e aperta enviar. Enquanto você não salvar o seu, vale o texto padrão do sistema."
      />
      {templates.isLoading ? (
        <LoadingCard label="Carregando as mensagens de WhatsApp" />
      ) : templates.error ? (
        <ErrorCard message={getApiError(templates.error)} />
      ) : (
        <div className="space-y-6">
          {(templates.data ?? []).map((template) => (
            // A chave muda quando o texto salvo muda, e o editor recomeça dele.
            <TemplateEditor
              key={`${template.event}:${template.updatedAt ?? 'padrão'}`}
              template={template}
              canEdit={canEdit}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function TemplateEditor({ template, canEdit }: { template: WhatsAppTemplate; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const field = useRef<HTMLTextAreaElement>(null);
  const [body, setBody] = useState(template.body);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const changed = body !== template.body;

  function insert(name: string) {
    const token = `{{${name}}}`;
    const element = field.current;
    const start = element?.selectionStart ?? body.length;
    const end = element?.selectionEnd ?? body.length;
    setBody(`${body.slice(0, start)}${token}${body.slice(end)}`);
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(start + token.length, start + token.length);
    });
  }

  async function run(action: () => Promise<WhatsAppTemplate>, done: string) {
    setError(undefined);
    setSaving(true);
    try {
      const saved = await action();
      queryClient.setQueryData<WhatsAppTemplate[]>(['company', 'whatsapp-templates'], (current) =>
        (current ?? []).map((item) => (item.event === saved.event ? saved : item)),
      );
      setBody(saved.body);
      snackbar.success(done);
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <article className="rounded-2xl border border-surface-border bg-surface-card p-5">
      <div className="mb-4 border-b border-surface-border pb-3 [&>header]:mb-2 [&>header]:border-0 [&>header]:pb-0">
        <ContentCardHeader
          title={template.label}
          icon={<CardIconMessageCircle className="size-4" />}
        />
        <StatusPill tone={template.custom ? 'positive' : 'neutral'}>
          {template.custom ? 'Texto da empresa' : 'Texto padrão do sistema'}
        </StatusPill>
      </div>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-3">
          <Textarea
            ref={field}
            label="Mensagem"
            rows={7}
            value={body}
            readOnly={!canEdit}
            onChange={(event) => setBody(event.target.value)}
            hint="Texto simples. No WhatsApp, *asteriscos* deixam em negrito."
          />
          {canEdit ? (
            <div>
              <span className="mb-2 block text-xs font-semibold text-content/70">
                Inserir no cursor
              </span>
              <div className="flex flex-wrap gap-1.5">
                {template.variables.map((name) => (
                  <button
                    key={name}
                    type="button"
                    onClick={() => insert(name)}
                    className="cursor-pointer rounded-lg border border-surface-border px-2.5 py-1 text-xs text-content-muted transition hover:border-brand-500 hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
                  >
                    {variableGuide[name]?.label ?? name}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
        <div>
          <span className="mb-2 block text-xs font-semibold text-content/70">
            Como o cliente lê
          </span>
          <p
            aria-label="Prévia da mensagem"
            className="whitespace-pre-wrap rounded-xl rounded-tl-sm border border-positive/30 bg-positive/10 px-4 py-3 text-sm leading-6"
          >
            {preview(body)}
          </p>
        </div>
      </div>
      {error ? <RequestAlert message={error} /> : null}
      {canEdit ? (
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          {template.custom ? (
            <Button
              variant="outline"
              disabled={saving}
              onClick={() =>
                void run(
                  () => whatsappTemplatesApi.reset(template.event),
                  'A mensagem voltou ao texto padrão.',
                )
              }
            >
              <RotateCcw className="size-4" /> Voltar ao padrão
            </Button>
          ) : null}
          <Button
            disabled={!changed || saving}
            onClick={() =>
              void run(() => whatsappTemplatesApi.save(template.event, body), 'Mensagem salva.')
            }
          >
            Salvar mensagem
          </Button>
        </div>
      ) : null}
    </article>
  );
}
